import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { Calendar } from '@fullcalendar/core'
import resourceTimelinePlugin from '@fullcalendar/resource-timeline'
import interactionPlugin, { Draggable } from '@fullcalendar/interaction'

export default class extends Controller {
  static targets = ["calendar", "teamMember", "searchInput", "roleLabel", "columnToggle", "fullscreenToggle", "emptyRowsToggle"]
  static values = {
    startDate: String,
    endDate: String,
    projects: Array,
    assignments: Array,
    unavailabilities: Object
  }

  connect() {
    this.showColumnDetails = false // Default to hiding PM/client details
    this.isFullscreen = false // Default to normal mode
    this.hideEmptyRows = false // Default to showing all projects
    this.currentRoleFilter = '' // Initialize role filter state
    this.hoveredUser = null // User currently being hovered
    this.draggedUser = null // User currently being dragged
    
    this.initializeCalendar()
    this.setupDragAndDrop()
    this.setupHoverEffects()
    
    // Set up debounced search for client-side filtering
    this.debouncedApplyFilters = this.debounce(this.applyFilters.bind(this), 300)
    
    // Add keyboard support for escape key
    this.keydownHandler = this.handleKeydown.bind(this)
    document.addEventListener('keydown', this.keydownHandler)
    
    // Add CSS styles for dimming effects
    this.addDimmingStyles()
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy()
    }
    
    // Exit fullscreen mode if active
    if (this.isFullscreen) {
      this.toggleFullscreen()
    }
    
    // Remove keyboard event listener
    if (this.keydownHandler) {
      document.removeEventListener('keydown', this.keydownHandler)
    }
    
    // Clean up timers and observers
    if (this.dragDetectionTimer) {
      clearTimeout(this.dragDetectionTimer)
    }
    if (this.dragObserver) {
      this.dragObserver.disconnect()
    }
    if (this.globalMouseUpHandler) {
      document.removeEventListener('mouseup', this.globalMouseUpHandler)
    }
    
    // Clean up state
    this.hoveredUser = null
    this.draggedUser = null
    
    // Remove dimming styles
    this.removeDimmingStyles()
  }

  handleKeydown(event) {
    // Exit fullscreen mode on Escape key
    if (event.key === 'Escape' && this.isFullscreen) {
      this.toggleFullscreen()
    }
  }

  initializeCalendar() {
    if (!this.hasCalendarTarget) return

    try {
      // Convert projects data to FullCalendar resources
      const resources = this.projectsValue.map(project => ({
        id: project.id.toString(),
        title: project.name,
        extendedProps: {
          clientName: project.client.name,
          projectName: project.name,
          manager: project.manager ? `${project.manager.first_name} ${project.manager.last_name}` : 'Unassigned'
        }
      }))
      
      // Sort resources alphabetically by project name (in case server order gets mixed up)
      resources.sort((a, b) => {
        const aName = a.extendedProps?.projectName || a.title || ''
        const bName = b.extendedProps?.projectName || b.title || ''
        return aName.localeCompare(bName)
      })

    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [resourceTimelinePlugin, interactionPlugin],
      
      // GPL License key for open source project
      schedulerLicenseKey: 'GPL-My-Project-Is-Open-Source',
      
      // View configuration - timeline with projects as rows
      initialView: 'resourceTimelineWeek',
      headerToolbar: false, // We have our own navigation
      
      // Timeline view configuration - exactly 7 day columns
      views: {
        resourceTimelineWeek: {
          type: 'resourceTimeline',
          duration: { weeks: 1 },
          slotDuration: { days: 1 },
          slotLabelInterval: { days: 1 }
        }
      },
      
      // Date configuration
      validRange: {
        start: this.startDateValue,
        end: this.endDateValue
      },
      
      // Week starts on Monday to match our week selector
      firstDay: 1,
      
      // Resource configuration for timeline
      resources: resources,
      resourceAreaWidth: '300px',
      resourceLabelText: 'Projects',
      
      // Event configuration
      editable: true,
      droppable: true,
      selectable: false,
      selectMirror: false,
      eventInteractive: true, // Ensure events are interactive
      eventStartEditable: false, // Prevent dragging start time
      eventDurationEditable: true, // Allow resizing to extend assignments
      
      // Timeline configuration - projects as rows, days as columns
      height: 'auto',
      slotMinWidth: 120,
      
      // Remove time-based indicators
      businessHours: false,
      nowIndicator: false,
      
      // Day formatting for timeline headers
      slotLabelFormat: {
        weekday: 'short',
        month: 'numeric',
        day: 'numeric'
      },
      
      // Make it more compact
      resourceAreaHeaderContent: 'Projects',
      
      // Ensure resources maintain alphabetical order
      resourceOrder: function(a, b) {
        const aName = a.extendedProps?.projectName || a.title || ''
        const bName = b.extendedProps?.projectName || b.title || ''
        return aName.localeCompare(bName)
      },
      
      // Custom resource rendering
      resourceLabelContent: (arg) => {
        return this.renderResourceLabel(arg)
      },
      
      // Receive external elements (from FullCalendar Draggable)
      eventReceive: (info) => {
        this.handleExternalDrop(info)
      },
      
      // Handle existing event drops
      eventDrop: (info) => {
        this.showToast(`Moved ${info.event.title}`, 'info')
      },
      
      // Handle event resizing
      eventResize: (info) => {
        this.handleEventResize(info)
      },
      
      // Handle event clicks
      eventClick: (info) => {
        this.handleEventClick(info)
      },
      
      // Event rendering and visual feedback
      eventDidMount: (info) => {
        // Make events look clickable but also resizable
        info.el.style.cursor = 'pointer'
        info.el.title = `Click to remove ${info.event.title} from this project, or drag edges to extend assignment`
        
        // Ensure the event can be properly targeted for dimming effects
        if (info.event.extendedProps.userId) {
          info.el.setAttribute('data-user-id', info.event.extendedProps.userId)
        }
      },
      
      // Hover effects on timeline events
      eventMouseEnter: (info) => {
        this.handleTimelineEventHoverStart(info)
      },
      
      eventMouseLeave: (info) => {
        this.handleTimelineEventHoverEnd(info)
      },
      
      // Drag-and-drop visual feedback
      eventDragStart: (info) => {
        this.handleTimelineEventDragStart(info)
      },
      
      eventDragStop: (info) => {
        this.handleTimelineEventDragEnd(info)
      }
    })

    this.calendar.render()
    
    // Load existing assignments
    this.loadExistingAssignments()
    } catch (error) {
      console.error("Error initializing calendar:", error)
    }
  }

  setupDragAndDrop() {
    // Set up FullCalendar draggable for team members
    if (this.teamMemberTargets.length > 0) {
      const containerEl = this.element.querySelector('#team-members-list')
      
      const draggableInstance = new Draggable(containerEl, {
        itemSelector: '.team-member-item',
        eventData: (eventEl) => {
          const userData = {
            id: eventEl.dataset.userId,
            name: eventEl.dataset.userName,
            role: eventEl.dataset.userRole
          }
          
          return {
            title: userData.name,
            duration: '04:00:00',
            backgroundColor: this.getColorForRole(userData.role),
            borderColor: this.getColorForRole(userData.role),
            extendedProps: {
              userId: userData.id,
              userRole: userData.role,
              assignmentType: 'manual'
            }
          }
        },
        
        // Improve drag performance and maintain dimming effects
        longPressDelay: 100, // Faster drag initiation
        eventOverlapPrevention: false, // Better performance
        
        // Add FullCalendar drag feedback
        dragStart: (info) => {
          const eventEl = info.element
          const userId = eventEl.dataset.userId
          
          // Set dragged user
          this.draggedUser = String(userId)
          
          // Add drag styling
          eventEl.classList.add('drag-active')
          
          // Apply effects (drag takes precedence over hover)
          this.applyEffectsForActiveUser()
        },
        
        dragStop: (info) => {
          const eventEl = info.element
          const userId = eventEl.dataset.userId
          
          // Remove drag styling immediately
          eventEl.classList.remove('drag-active', 'hover-active')
          
          // Check if a drop occurred after a short delay
          setTimeout(() => {
            // Only clear if we're still dragging this user (no successful drop)
            if (this.draggedUser === String(userId)) {
              this.draggedUser = null
              this.applyEffectsForActiveUser()
            }
          }, 150)
        }
      })
      
    }
  }

  async handleExternalDrop(info) {
    try {
      // Get event data from FullCalendar
      const eventData = info.event.extendedProps
      
      // Get the resource from the event (for eventReceive callback)
      const resources = info.event.getResources()
      const resource = resources[0] // Should be only one resource for timeline view
      
      if (!resource) {
        console.error('No resource found for dropped event')
        this.showToast('Error: Could not determine project for assignment', 'error')
        info.event.remove()
        
        // Clear drag state and effects on error
        if (eventData?.userId) {
          this.cleanupDragState(eventData.userId)
        }
        return
      }
      
      const projectName = resource.extendedProps?.projectName || resource.title || 'Unknown Project'
      const projectId = resource.id
      

      
      // Get the assignment date from the event
      const assignmentDate = info.event.start
      
      // Remove the temporary event that FullCalendar created
      info.event.remove()
      
      // Save assignment to backend
      const response = await this.createAssignment(
        eventData.userId,
        projectId,
        assignmentDate
      )
      
      if (response.success) {
        // Create event with the backend-generated data
        this.calendar.addEvent(response.assignment)
        this.showToast(response.message, 'success')
        
        // Refresh resource visibility if we're hiding empty rows
        if (this.hideEmptyRows) {
          setTimeout(() => this.filterResourcesByActivity(), 100)
        }
      } else {
        this.showToast(`Error: ${response.errors.join(', ')}`, 'error')
      }
      
      // Clear drag state and effects after successful drop processing
      const droppedUserId = eventData.userId
      this.cleanupDragState(droppedUserId)
      
    } catch (error) {
      console.error('Error handling external drop:', error)
      this.showToast('Error assigning team member', 'error')
      
      // Clear drag state and effects on error
      if (eventData?.userId) {
        this.cleanupDragState(eventData.userId)
      }
    }
  }



  async handleEventClick(info) {
    const event = info.event
    const assignmentType = event.extendedProps.assignmentType
    
    // Check if this is a click on the event content (not resize handles)
    const target = info.jsEvent.target
    const isResizeHandle = target.classList.contains('fc-event-resizer') || 
                          target.closest('.fc-event-resizer')
    
    if (isResizeHandle) {
      // Don't handle clicks on resize handles
      return
    }
    
    if (assignmentType === 'persisted') {
      // Use shared assignment modal controller
      const modalController = this.application.getControllerForElementAndIdentifier(
        document.querySelector('[data-controller*="assignment-modal"]'),
        'assignment-modal'
      )
      
      if (modalController) {
        const projectName = event.getResources()[0]?.extendedProps?.projectName || 'Unknown Project'
        modalController.show(event, projectName)
      }
      
      // Only prevent default if we handled the click
      info.jsEvent.preventDefault()
    }
  }

  async handleEventResize(info) {
    const event = info.event
    const assignmentType = event.extendedProps.assignmentType
    
    if (assignmentType !== 'persisted') {
      return
    }
    
    try {
      // Format dates without timezone conversion to preserve the local date
      const startDateObj = info.event.start
      const startDate = `${startDateObj.getFullYear()}-${String(startDateObj.getMonth() + 1).padStart(2, '0')}-${String(startDateObj.getDate()).padStart(2, '0')}`
      
      // FullCalendar end dates are exclusive for all-day events
      // If end is July 24, the event should include up to July 23 (subtract 1 day)
      const exclusiveEndDate = new Date(info.event.end)
      exclusiveEndDate.setDate(exclusiveEndDate.getDate() - 1)
      const endDate = `${exclusiveEndDate.getFullYear()}-${String(exclusiveEndDate.getMonth() + 1).padStart(2, '0')}-${String(exclusiveEndDate.getDate()).padStart(2, '0')}`
      
      // Save resize to backend
      const response = await this.updateAssignment(
        event.extendedProps.assignmentId,
        startDate,
        endDate
      )
      
      if (response.success) {
        // Remove the old event and add the new one with updated data
        event.remove()
        this.calendar.addEvent(response.assignment)
        // No success toast - only show warnings/errors
      } else {
        // Revert the resize on error
        info.revert()
        this.showToast(`Error: ${response.errors.join(', ')}`, 'error')
      }
      
    } catch (error) {
      console.error('Error handling event resize:', error)
      // Revert the resize on error
      info.revert()
      this.showToast('Error updating assignment', 'error')
    }
  }

  getColorForRole(role) {
    const colors = {
      'admin': '#ef4444',      // red
      'manager': '#f59e0b',    // amber
      'developer': '#10b981',  // emerald
      'designer': '#8b5cf6',   // violet
      'qa': '#06b6d4',         // cyan
      'employee': '#6b7280'    // gray
    }
    return colors[role] || colors['employee']
  }

  search() {
    this.debouncedApplyFilters()
  }

  filterByRole(event) {
    const role = event.currentTarget.dataset.role
    const roleLabel = event.currentTarget.textContent
    
    // Update UI
    this.roleLabelTarget.textContent = roleLabel
    
    // Store current filter
    this.currentRoleFilter = role
    
    // Apply filters client-side
    this.applyFilters()
  }

  applyFilters() {
    const searchQuery = this.searchInputTarget.value.toLowerCase().trim()
    const roleFilter = this.currentRoleFilter || ''
    
    // Get all team member elements
    const teamMembers = this.teamMemberTargets
    let visibleCount = 0
    
    teamMembers.forEach(member => {
      const userName = member.dataset.userName.toLowerCase()
      const userRole = member.dataset.userRole
      
      // Check search filter
      const matchesSearch = !searchQuery || userName.includes(searchQuery)
      
      // Check role filter
      const matchesRole = !roleFilter || userRole === roleFilter
      
      // Show/hide based on filters
      const shouldShow = matchesSearch && matchesRole
      
      if (shouldShow) {
        member.style.display = 'block'
        visibleCount++
      } else {
        member.style.display = 'none'
      }
    })
    
    // Re-setup hover effects for visible members after filtering
    this.setupHoverEffects()
    
    // Show/hide "no members found" message
    this.updateNoMembersMessage(visibleCount)
  }

  updateNoMembersMessage(visibleCount) {
    const container = document.querySelector('#team-members-list')
    if (!container) return
    
    let noMembersMessage = container.querySelector('.no-members-message')
    
    if (visibleCount === 0) {
      // Show "no members found" message
      if (!noMembersMessage) {
        noMembersMessage = document.createElement('div')
        noMembersMessage.className = 'no-members-message text-center py-8 text-base-content/60 w-full'
        noMembersMessage.innerHTML = `
          <iconify-icon icon="ph:users" class="text-3xl mb-2"></iconify-icon>
          <div class="text-sm">No team members found</div>
        `
        container.appendChild(noMembersMessage)
      }
    } else {
      // Hide "no members found" message
      if (noMembersMessage) {
        noMembersMessage.remove()
      }
    }
  }

  showToast(message, type = 'info') {
    // Create a simple toast notification
    const toast = document.createElement('div')
    toast.className = `alert alert-${type} fixed top-4 right-4 z-50 w-auto max-w-sm`
    toast.innerHTML = `
      <iconify-icon icon="ph:check-circle" class="mr-2"></iconify-icon>
      <span>${message}</span>
    `
    
    document.body.appendChild(toast)
    
    // Remove after 3 seconds
    setTimeout(() => {
      toast.remove()
    }, 3000)
  }

  debounce(func, delay) {
    let timeoutId
    return function (...args) {
      clearTimeout(timeoutId)
      timeoutId = setTimeout(() => func.apply(this, args), delay)
    }
  }

  renderResourceLabel(arg) {
    const resource = arg.resource
    const props = resource.extendedProps || {}
    const projectName = props.projectName || resource.title || 'Unknown Project'
    const clientName = props.clientName || 'No client'
    const manager = props.manager || 'Unassigned'
    
    if (this.showColumnDetails) {
      return {
        html: `
          <div class="flex items-center gap-2 p-2">
            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm truncate">${projectName}</div>
              <div class="text-xs text-base-content/60 truncate">${clientName}</div>
              <div class="text-xs text-base-content/50">${manager}</div>
            </div>
          </div>
        `
      }
    } else {
      return {
        html: `
          <div class="flex items-center gap-2 p-2">
            <div class="flex-1 min-w-0">
              <div class="font-medium text-sm truncate">${projectName}</div>
            </div>
          </div>
        `
      }
    }
  }

  toggleColumnDetails() {
    this.showColumnDetails = !this.showColumnDetails
    
    // Update the button icon to reflect current state
    const icon = this.columnToggleTarget.querySelector('iconify-icon')
    if (this.showColumnDetails) {
      icon.setAttribute('icon', 'ph:arrows-in-line-horizontal')
      this.columnToggleTarget.title = 'Hide PM/Client display'
    } else {
      icon.setAttribute('icon', 'ph:arrows-out-line-horizontal')
      this.columnToggleTarget.title = 'Show PM/Client display'
    }
    
    // Refresh the calendar to update resource labels
    if (this.calendar) {
      this.calendar.render()
    }
  }

  toggleEmptyRows() {
    this.hideEmptyRows = !this.hideEmptyRows
    
    // Update the button icon and title to reflect current state
    const icon = this.emptyRowsToggleTarget.querySelector('iconify-icon')
    if (this.hideEmptyRows) {
      icon.setAttribute('icon', 'ph:hammer-fill')
      this.emptyRowsToggleTarget.title = 'Show all projects'
    } else {
      icon.setAttribute('icon', 'ph:hammer')
      this.emptyRowsToggleTarget.title = 'Hide empty projects'
    }
    
    // Use FullCalendar's API to filter resources
    this.filterResourcesByActivity()
  }

  filterResourcesByActivity() {
    if (!this.calendar) return
    
    // Get all resources and events using FullCalendar API
    const allResources = this.calendar.getResources()
    const allEvents = this.calendar.getEvents()
    
    if (this.hideEmptyRows) {
      // Build a set of resource IDs that have events
      const resourcesWithEvents = new Set()
      allEvents.forEach(event => {
        const resources = event.getResources()
        resources.forEach(resource => {
          resourcesWithEvents.add(resource.id)
        })
      })
      
      // Hide resources without events - need to hide both the label AND the timeline row
      allResources.forEach(resource => {
        const hasActivity = resourcesWithEvents.has(resource.id)
        
        // Find the resource label cell in the left column
        const resourceEl = this.calendar.el.querySelector(`[data-resource-id="${resource.id}"]`)
        if (resourceEl) {
          const labelRow = resourceEl.closest('tr')
          if (labelRow) {
            if (hasActivity) {
              labelRow.style.display = ''
            } else {
              labelRow.style.display = 'none'
            }
          }
        }
        
        // Also find and hide the corresponding timeline lane (where events are displayed)
        const timelineLanes = this.calendar.el.querySelectorAll('.fc-timeline-lane[data-resource-id="' + resource.id + '"]')
        timelineLanes.forEach(lane => {
          const laneRow = lane.closest('tr')
          if (laneRow) {
            if (hasActivity) {
              laneRow.style.display = ''
            } else {
              laneRow.style.display = 'none'
            }
          }
        })
      })
    } else {
      // Show all resources by removing any display:none from both label and timeline rows
      allResources.forEach(resource => {
        // Show resource label row
        const resourceEl = this.calendar.el.querySelector(`[data-resource-id="${resource.id}"]`)
        if (resourceEl) {
          const labelRow = resourceEl.closest('tr')
          if (labelRow) {
            labelRow.style.display = ''
          }
        }
        
        // Show timeline lane row
        const timelineLanes = this.calendar.el.querySelectorAll('.fc-timeline-lane[data-resource-id="' + resource.id + '"]')
        timelineLanes.forEach(lane => {
          const laneRow = lane.closest('tr')
          if (laneRow) {
            laneRow.style.display = ''
          }
        })
      })
    }
  }

  toggleFullscreen() {
    this.isFullscreen = !this.isFullscreen
    
    // Get elements to hide/show
    const platformHeader = document.querySelector('[data-controller="header"]') || document.querySelector('.navbar') || document.querySelector('header')
    const pageHeader = document.querySelector('#page-header') // The header section with title and week selector
    
    if (this.isFullscreen) {
      // Enter fullscreen mode
      if (platformHeader) {
        platformHeader.style.display = 'none'
      }
      if (pageHeader) {
        pageHeader.style.display = 'none'
      }
      
      // Update body to remove any top padding/margin
      document.body.style.paddingTop = '0'
      document.body.style.marginTop = '0'
      
      // Update button
      const icon = this.fullscreenToggleTarget.querySelector('iconify-icon')
      icon.setAttribute('icon', 'ph:arrows-in')
      this.fullscreenToggleTarget.title = 'Exit fullscreen mode'
      
      // Add fullscreen class to main container for styling
      this.element.classList.add('fullscreen-mode')
      
    } else {
      // Exit fullscreen mode
      if (platformHeader) {
        platformHeader.style.display = ''
      }
      if (pageHeader) {
        pageHeader.style.display = ''
      }
      
      // Reset body styling
      document.body.style.paddingTop = ''
      document.body.style.marginTop = ''
      
      // Update button
      const icon = this.fullscreenToggleTarget.querySelector('iconify-icon')
      icon.setAttribute('icon', 'ph:arrows-out')
      this.fullscreenToggleTarget.title = 'Enter fullscreen mode'
      
      // Remove fullscreen class
      this.element.classList.remove('fullscreen-mode')
    }
    
    // Refresh calendar to adjust to new dimensions
    setTimeout(() => {
      if (this.calendar) {
        this.calendar.updateSize()
      }
    }, 100)
  }

  loadExistingAssignments() {
    // Load assignments from the passed data value
    if (this.assignmentsValue && Array.isArray(this.assignmentsValue)) {
      this.assignmentsValue.forEach(assignment => {
        this.calendar.addEvent(assignment)
      })
    }
  }

  async createAssignment(userId, projectId, assignmentDate) {
    // Format date without timezone conversion to preserve the local date
    const formattedDate = `${assignmentDate.getFullYear()}-${String(assignmentDate.getMonth() + 1).padStart(2, '0')}-${String(assignmentDate.getDate()).padStart(2, '0')}`
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        console.error('CSRF token not found')
        return { success: false, errors: ['CSRF token not found'] }
      }
      
      const response = await fetch('/team/create_assignment', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          user_id: userId,
          project_id: projectId,
          assignment_date: formattedDate
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Response not ok:', response.status, errorText)
        return { success: false, errors: [`Server error: ${response.status}`] }
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error creating assignment:', error)
      return { success: false, errors: ['Network error occurred'] }
    }
  }

  async deleteAssignment(assignmentId, assignmentIds = null) {
    try {
      const requestBody = {
        assignment_id: assignmentId
      }
      
      // Include assignment_ids for multi-day assignments
      if (assignmentIds && Array.isArray(assignmentIds)) {
        requestBody.assignment_ids = assignmentIds
      }
      
      const response = await fetch('/team/delete_assignment', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify(requestBody)
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Delete response not ok:', response.status, errorText)
        return { success: false, errors: [`Server error: ${response.status}`] }
      }

      const data = await response.json()
      
      // Refresh resource visibility if we're hiding empty rows
      if (this.hideEmptyRows) {
        setTimeout(() => this.filterResourcesByActivity(), 100)
      }
      
      return data
    } catch (error) {
      console.error('Error deleting assignment:', error)
      return { success: false, errors: ['Network error occurred'] }
    }
  }

  async updateAssignment(assignmentId, startDate, endDate) {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (!csrfToken) {
        console.error('CSRF token not found')
        return { success: false, errors: ['CSRF token not found'] }
      }
      
      const response = await fetch('/team/update_assignment', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          assignment_id: assignmentId,
          start_date: startDate,
          end_date: endDate
        })
      })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Response not ok:', response.status, errorText)
        return { success: false, errors: [`Server error: ${response.status}`] }
      }

      const data = await response.json()
      return data
    } catch (error) {
      console.error('Error updating assignment:', error)
      return { success: false, errors: ['Network error occurred'] }
    }
  }

  handleToast(event) {
    // Handle toast events from the shared assignment modal
    const { message, type } = event.detail
    this.showToast(message, type)
  }

  handleAssignmentDeleted(event) {
    // Handle assignment deletion from the modal
    // Refresh resource visibility if we're hiding empty rows
    if (this.hideEmptyRows) {
      setTimeout(() => this.filterResourcesByActivity(), 100)
    }
  }

  addDimmingStyles() {
    // Add CSS styles for dimming effects
    if (!document.getElementById('team-planning-dimming-styles')) {
      const style = document.createElement('style')
      style.id = 'team-planning-dimming-styles'
      style.textContent = `
        /* Dimming effects for timeline events - optimized for performance */
        .fc-event {
          transition: opacity 0.15s ease-out, box-shadow 0.15s ease-out, transform 0.1s ease-out;
          will-change: opacity, box-shadow, transform; /* Optimize for animations */
        }
        
        .fc-event.dimmed {
          opacity: 0.2 !important;
        }
        
        .fc-event.highlighted {
          opacity: 1 !important;
          box-shadow: 0 0 8px rgba(59, 130, 246, 0.5) !important;
        }
        
        /* Team member highlight effects - optimized for better drag performance */
        .team-member-item {
          transition: transform 0.1s ease-out, box-shadow 0.1s ease-out, border-color 0.1s ease-out;
          will-change: transform, box-shadow; /* Optimize for animations */
        }
        
        .team-member-item.drag-active {
          transform: translateZ(0) scale(1.05); /* Force hardware acceleration */
          box-shadow: 0 4px 20px rgba(59, 130, 246, 0.3);
          border-color: rgb(59, 130, 246);
          z-index: 1000;
          position: relative;
          cursor: grabbing !important; /* Better visual feedback */
        }
        
        .team-member-item.hover-active {
          transform: translateZ(0) scale(1.02); /* Force hardware acceleration */
          box-shadow: 0 2px 12px rgba(59, 130, 246, 0.2);
          border-color: rgb(59, 130, 246, 0.7);
          cursor: grab; /* Better visual feedback */
        }
        
        /* Unavailable column shading */
        .fc-timeline-slot.unavailable-column {
          background-color: rgba(239, 68, 68, 0.1) !important;
          position: relative;
        }
        
        .fc-timeline-slot.unavailable-column::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: repeating-linear-gradient(
            45deg,
            transparent,
            transparent 2px,
            rgba(239, 68, 68, 0.2) 2px,
            rgba(239, 68, 68, 0.2) 4px
          );
          pointer-events: none;
          z-index: 1;
        }
      `
      document.head.appendChild(style)
    }
  }

  removeDimmingStyles() {
    const style = document.getElementById('team-planning-dimming-styles')
    if (style) {
      style.remove()
    }
  }

  setupHoverEffects() {
    // Add hover event listeners to team member items
    // Note: Drag events are handled by FullCalendar Draggable, not native browser events
    this.teamMemberTargets.forEach(memberEl => {
      // Remove native draggable attribute to prevent conflicts with FullCalendar
      memberEl.removeAttribute('draggable')
      
      // Check if listeners are already attached to avoid duplicates
      if (!memberEl.hasAttribute('data-hover-listeners-attached')) {
        memberEl.addEventListener('mouseenter', (e) => this.handleTeamMemberHoverStart(e))
        memberEl.addEventListener('mouseleave', (e) => this.handleTeamMemberHoverEnd(e))
        
        // Add mousedown/mouseup to detect when dragging starts/ends
        memberEl.addEventListener('mousedown', (e) => this.handleTeamMemberMouseDown(e))
        memberEl.addEventListener('mouseup', (e) => this.handleTeamMemberMouseUp(e))
        
        // Mark as having listeners attached
        memberEl.setAttribute('data-hover-listeners-attached', 'true')
      }
    })
  }

  handleTeamMemberMouseDown(event) {
    const userId = event.currentTarget.dataset.userId
    const userName = event.currentTarget.dataset.userName
    const element = event.currentTarget
    
    // Set a flag that we're potentially starting a drag
    this.potentialDragUser = String(userId)
    
    // Watch for CSS changes on this element to detect when FullCalendar starts dragging
    this.setupDragObserver(element, userId)
    
    // Add global mouseup listener to catch cancelled drags
    this.setupGlobalMouseUp(userId)
    
    // Start a timer to detect if this becomes a drag
    this.dragDetectionTimer = setTimeout(() => {
      if (this.potentialDragUser === String(userId)) {
        this.draggedUser = String(userId)
        this.applyEffectsForActiveUser()
      }
    }, 100) // Small delay to detect if mouse moves (indicating drag)
  }

  setupGlobalMouseUp(userId) {
    // Remove any existing global listener
    if (this.globalMouseUpHandler) {
      document.removeEventListener('mouseup', this.globalMouseUpHandler)
    }
    
    // Add global mouseup listener to catch drags that end outside the element
    this.globalMouseUpHandler = (event) => {
      this.cleanupDragState(userId)
    }
    
    document.addEventListener('mouseup', this.globalMouseUpHandler, { once: true })
  }

  cleanupDragState(userId) {
    // Clear potential drag
    this.potentialDragUser = null
    
    // Clear timer
    if (this.dragDetectionTimer) {
      clearTimeout(this.dragDetectionTimer)
      this.dragDetectionTimer = null
    }
    
    // Clear observer
    if (this.dragObserver) {
      this.dragObserver.disconnect()
      this.dragObserver = null
    }
    
    // Remove global listener
    if (this.globalMouseUpHandler) {
      document.removeEventListener('mouseup', this.globalMouseUpHandler)
      this.globalMouseUpHandler = null
    }
    
    // Clear dragged user if this was the dragged user
    if (this.draggedUser === String(userId)) {
      this.draggedUser = null
      this.applyEffectsForActiveUser()
    }
  }

  setupDragObserver(element, userId) {
    // Create a MutationObserver to watch for class/style changes during drag
    if (this.dragObserver) {
      this.dragObserver.disconnect()
    }
    
    this.dragObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes') {
          const target = mutation.target
          
          // Check if FullCalendar added any drag-related classes or styles
          const isBeingDragged = target.style.opacity !== '' || 
                               target.className.includes('dragging') || 
                               target.className.includes('drag') ||
                               target.style.transform !== ''
          
          if (isBeingDragged && !this.draggedUser) {
            this.draggedUser = String(userId)
            this.applyEffectsForActiveUser()
          }
        }
      })
    })
    
    this.dragObserver.observe(element, {
      attributes: true,
      attributeFilter: ['class', 'style']
    })
  }

  handleTeamMemberMouseUp(event) {
    const userId = event.currentTarget.dataset.userId
    
    // Use centralized cleanup
    this.cleanupDragState(userId)
  }

  // Helper method to get the currently active user (drag takes precedence over hover)
  getActiveUser() {
    return this.draggedUser || this.hoveredUser
  }

  // Helper method to apply effects for the active user
  applyEffectsForActiveUser() {
    const activeUser = this.getActiveUser()
    
    if (activeUser) {
      this.dimOtherUserAssignments(activeUser)
      this.shadeUnavailableColumns(activeUser)
    } else {
      this.clearDimmingEffects()
      this.clearUnavailableShading()
    }
  }

  handleTeamMemberHoverStart(event) {
    const userId = event.currentTarget.dataset.userId
    
    // Set hovered user and add visual styling
    this.hoveredUser = String(userId)
    event.currentTarget.classList.add('hover-active')
    
    // Apply effects based on current state (drag takes precedence)
    this.applyEffectsForActiveUser()
  }



  handleTeamMemberHoverEnd(event) {
    const userId = event.currentTarget.dataset.userId
    
    // Remove hover styling immediately
    event.currentTarget.classList.remove('hover-active')
    
    // Clear hovered user only if this was the hovered user
    if (this.hoveredUser === String(userId)) {
      this.hoveredUser = null
      
      // Reapply effects based on current state (may still have dragged user)
      this.applyEffectsForActiveUser()
    }
  }



  dimOtherUserAssignments(activeUserId) {
    if (!this.calendar) return
    
    const allEvents = this.calendar.getEvents()
    
    // Convert activeUserId to string for consistent comparison
    const activeUserIdStr = String(activeUserId)
    
    allEvents.forEach(event => {
      const eventUserId = event.extendedProps?.userId
      const eventUserIdStr = String(eventUserId)
      
      if (eventUserId && eventUserIdStr !== activeUserIdStr) {
        // Dim events from other users
        const newClassNames = [...(event.classNames || []).filter(c => !['dimmed', 'highlighted'].includes(c)), 'dimmed']
        event.setProp('classNames', newClassNames)
      } else if (eventUserId && eventUserIdStr === activeUserIdStr) {
        // Highlight events from the active user
        const newClassNames = [...(event.classNames || []).filter(c => !['dimmed', 'highlighted'].includes(c)), 'highlighted']
        event.setProp('classNames', newClassNames)
      }
    })
  }

  clearDimmingEffects() {
    if (!this.calendar) return
    
    const allEvents = this.calendar.getEvents()
    
    allEvents.forEach(event => {
      // Remove dimming and highlighting classes
      const cleanClassNames = (event.classNames || []).filter(c => !['dimmed', 'highlighted'].includes(c))
      event.setProp('classNames', cleanClassNames)
    })
  }

  handleTimelineEventHoverStart(info) {
    const event = info.event
    const userId = event.extendedProps?.userId
    
    if (!userId) return
    
    // Set hovered user
    this.hoveredUser = String(userId)
    
    // Add visual feedback to the hovered event
    info.el.style.transform = 'scale(1.02)'
    info.el.style.zIndex = '1000'
    
    // Apply effects based on current state (drag takes precedence)
    this.applyEffectsForActiveUser()
  }

  handleTimelineEventHoverEnd(info) {
    const event = info.event
    const userId = event.extendedProps?.userId
    
    if (!userId) return
    
    // Remove visual feedback from the event immediately
    info.el.style.transform = ''
    info.el.style.zIndex = ''
    
    // Clear hovered user only if this was the hovered user
    if (this.hoveredUser === String(userId)) {
      this.hoveredUser = null
      
      // Reapply effects based on current state (may still have dragged user)
      this.applyEffectsForActiveUser()
    }
  }

  handleTimelineEventDragStart(info) {
    const event = info.event
    const userId = event.extendedProps?.userId
    
    if (!userId) return
    
    // Set dragged user
    this.draggedUser = String(userId)
    
    // Add visual feedback to the dragged event
    info.el.style.opacity = '0.8'
    info.el.style.transform = 'translateZ(0) scale(1.05)'
    info.el.style.zIndex = '1000'
    info.el.style.boxShadow = '0 4px 20px rgba(59, 130, 246, 0.3)'
    info.el.style.cursor = 'grabbing'
    
    // Apply effects (drag takes precedence over hover)
    this.applyEffectsForActiveUser()
  }

  handleTimelineEventDragEnd(info) {
    const event = info.event
    const userId = event.extendedProps?.userId
    
    if (!userId) return
    
    // Clear dragged user only if this was the dragged user
    if (this.draggedUser === String(userId)) {
      this.draggedUser = null
    }
    
    // Remove visual feedback from the event
    info.el.style.opacity = ''
    info.el.style.transform = ''
    info.el.style.zIndex = ''
    info.el.style.boxShadow = ''
    info.el.style.cursor = ''
    
    // Reapply effects based on current state (may still have hovered user)
    this.applyEffectsForActiveUser()
  }

  shadeUnavailableColumns(userId) {
    if (!this.calendar || !this.unavailabilitiesValue) {
      return
    }
    
    const userUnavailabilities = this.unavailabilitiesValue[String(userId)]
    if (!userUnavailabilities || userUnavailabilities.length === 0) {
      return
    }
    
    // Get the calendar view element
    const calendarEl = this.calendar.el
    if (!calendarEl) {
      return
    }
    
    // Find all date column headers in the timeline
    const dateHeaders = calendarEl.querySelectorAll('.fc-timeline-slot[data-date]')
    
    if (dateHeaders.length === 0) {
      // Try alternative selectors for different calendar views
      const altHeaders1 = calendarEl.querySelectorAll('.fc-col-header-cell[data-date]')
      const altHeaders2 = calendarEl.querySelectorAll('.fc-daygrid-day[data-date]')
      
      // Try shading the alternative headers
      const allAltHeaders = Array.from(altHeaders1).concat(Array.from(altHeaders2))
      allAltHeaders.forEach(header => {
        const columnDate = header.getAttribute('data-date')
        if (userUnavailabilities.includes(columnDate)) {
          header.classList.add('unavailable-column')
        }
      })
      return
    }
    
    dateHeaders.forEach(header => {
      const columnDate = header.getAttribute('data-date')
      
      if (userUnavailabilities.includes(columnDate)) {
        // Shade this column by adding a class
        header.classList.add('unavailable-column')
        
        // Also shade the corresponding data cells in the same column
        const columnIndex = Array.from(header.parentNode.children).indexOf(header)
        const dataCells = calendarEl.querySelectorAll(`.fc-timeline-lane .fc-timeline-slot:nth-child(${columnIndex + 1})`)
        dataCells.forEach(cell => {
          cell.classList.add('unavailable-column')
        })
      }
    })
  }

  clearUnavailableShading() {
    if (!this.calendar) return
    
    const calendarEl = this.calendar.el
    if (!calendarEl) return
    
    // Remove shading from all columns
    const shadedElements = calendarEl.querySelectorAll('.unavailable-column')
    shadedElements.forEach(element => {
      element.classList.remove('unavailable-column')
    })
  }


} 